import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Skeleton,
  TableSortLabel,
  TablePagination,
  useTheme,
  Chip,
  Button,
} from "@mui/material";
import Add from "@mui/icons-material/Add";
import { useNavigate } from "react-router-dom";
import ArrowForwardIos from "@mui/icons-material/ArrowForwardIos";
import { useGetPlanList } from "../../hooks/api/usePlan";
import AddPlanModal from "../Plans/AddPlanModal";
import type { Plan } from "../../Graphql/types/plan";
import { getPlanDetailPath } from "../../constants/routes";
import AdminScopeFilters, {
  ALL_BUSINESS_TYPES,
  type AdminScopeFilterValues,
} from "../common/AdminScopeFilters";
import AdminActiveFilterChips from "../common/AdminActiveFilterChips";
import { adminSurface, adminTableRowSx } from "../../styles/adminUiTokens";
import MembershipColumnFilterField from "./MembershipColumnFilterField";
import {
  compareSortValues,
  hasActiveFilters,
  matchesStatusFilter,
  matchesTextFilter,
  type SortOrder,
} from "./utils/membershipTableUtils";
import {
  formatBusinessTypeLabel,
  matchesEnumFilter,
} from "../common/adminEnumLabels";

type OrderBy = "name" | "description" | "active" | "businessType";
type Order = SortOrder;

const ROWS_PER_PAGE_OPTIONS = [5, 10, 25, 50];

type PlanFieldFilters = {
  name: string;
  description: string;
  businessType: string;
  status: string;
};

const EMPTY_PLAN_FILTERS: PlanFieldFilters = {
  name: "",
  description: "",
  businessType: "",
  status: "",
};

function getSortValue(p: Plan, field: OrderBy): string | number {
  if (field === "name") return p.name;
  if (field === "description") return p.description ?? "";
  if (field === "businessType") return p.businessType ?? "";
  return p.isActive ? 1 : 0;
}

function filterAndSort(
  list: Plan[],
  filters: PlanFieldFilters,
  businessType: string,
  orderBy: OrderBy,
  order: Order,
): Plan[] {
  let filtered = list.filter(
    (p) =>
      matchesTextFilter(p.name, filters.name) &&
      matchesTextFilter(p.description ?? "", filters.description) &&
      matchesEnumFilter(
        p.businessType ?? "",
        filters.businessType,
        formatBusinessTypeLabel,
      ) &&
      matchesStatusFilter(p.isActive, filters.status),
  );
  if (businessType) {
    filtered = filtered.filter((p) => p.businessType === businessType);
  }
  return filtered.sort((a, b) =>
    compareSortValues(
      getSortValue(a, orderBy),
      getSortValue(b, orderBy),
      order,
    ),
  );
}

const refetchPlans = (
  getPlanList: ReturnType<typeof useGetPlanList>,
  setPlans: (p: Plan[]) => void,
) => {
  getPlanList
    .mutateAsync({ activeOnly: false })
    .then((res) => setPlans(res.listPlans?.planList ?? []))
    .catch(() => {});
};

const PlanListTable: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [plans, setPlans] = useState<Plan[]>([]);
  const [fieldFilters, setFieldFilters] =
    useState<PlanFieldFilters>(EMPTY_PLAN_FILTERS);
  const [scopeFilters, setScopeFilters] = useState<AdminScopeFilterValues>({
    countryCode: "",
    businessType: "",
  });
  const [orderBy, setOrderBy] = useState<OrderBy>("name");
  const [order, setOrder] = useState<Order>("asc");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [addModalOpen, setAddModalOpen] = useState(false);

  const getPlanList = useGetPlanList();

  useEffect(() => {
    refetchPlans(getPlanList, setPlans);
  }, []);

  const handleAddSuccess = () => {
    refetchPlans(getPlanList, setPlans);
  };

  const sorted = useMemo(
    () =>
      filterAndSort(
        plans,
        fieldFilters,
        scopeFilters.businessType,
        orderBy,
        order,
      ),
    [plans, fieldFilters, scopeFilters.businessType, orderBy, order],
  );

  const updateFieldFilter = (key: keyof PlanFieldFilters, value: string) => {
    setFieldFilters((prev) => ({ ...prev, [key]: value }));
    setPage(0);
  };

  const paginated = useMemo(() => {
    const start = page * rowsPerPage;
    return sorted.slice(start, start + rowsPerPage);
  }, [sorted, page, rowsPerPage]);

  const handleSort = (field: OrderBy) => {
    const isAsc = orderBy === field && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(field);
    setPage(0);
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const isLoading = getPlanList.isPending && plans.length === 0;
  const isEmpty = !isLoading && plans.length === 0;
  const noSearchResults = !isEmpty && sorted.length === 0;

  return (
    <Box sx={{ width: "100%" }}>
      <Paper elevation={0} sx={{ ...adminSurface, width: "100%" }}>
        <Box
          sx={{
            px: 2,
            pt: 2,
            pb: 1,
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
          }}
        >
          <Box
            sx={{
              display: "flex",
              gap: 2,
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <AdminScopeFilters
              showCountry={false}
              values={scopeFilters}
              onChange={(next) => {
                setScopeFilters(next);
                setPage(0);
              }}
            />
            {hasActiveFilters(fieldFilters) && (
              <Button
                size="small"
                color="inherit"
                onClick={() => setFieldFilters(EMPTY_PLAN_FILTERS)}
              >
                Clear column filters
              </Button>
            )}
            <Box sx={{ flex: 1 }} />
            <Button
              variant="contained"
              size="medium"
              startIcon={<Add />}
              onClick={() => setAddModalOpen(true)}
            >
              Add
            </Button>
          </Box>
          <AdminActiveFilterChips
            values={scopeFilters}
            onClearBusinessType={() =>
              setScopeFilters((v) => ({
                ...v,
                businessType: ALL_BUSINESS_TYPES,
              }))
            }
            onClearAll={() =>
              setScopeFilters({ countryCode: "", businessType: "" })
            }
          />
        </Box>
        <AddPlanModal
          open={addModalOpen}
          onClose={() => setAddModalOpen(false)}
          onSuccess={handleAddSuccess}
        />

        <TableContainer sx={{ overflowX: "auto" }}>
          <Table size="medium" stickyHeader>
            <TableHead>
              <TableRow
                sx={{
                  bgcolor:
                    theme.palette.mode === "light" ? "grey.50" : "grey.900",
                }}
              >
                <TableCell
                  sx={{ fontWeight: 600, py: 2, fontSize: "0.875rem" }}
                >
                  <TableSortLabel
                    active={orderBy === "name"}
                    direction={orderBy === "name" ? order : "asc"}
                    onClick={() => handleSort("name")}
                  >
                    Name
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  sx={{ fontWeight: 600, py: 2, fontSize: "0.875rem" }}
                >
                  <TableSortLabel
                    active={orderBy === "description"}
                    direction={orderBy === "description" ? order : "asc"}
                    onClick={() => handleSort("description")}
                  >
                    Description
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  sx={{ fontWeight: 600, py: 2, fontSize: "0.875rem" }}
                >
                  <TableSortLabel
                    active={orderBy === "businessType"}
                    direction={orderBy === "businessType" ? order : "asc"}
                    onClick={() => handleSort("businessType")}
                  >
                    Industry
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  sx={{ fontWeight: 600, py: 2, fontSize: "0.875rem" }}
                >
                  <TableSortLabel
                    active={orderBy === "active"}
                    direction={orderBy === "active" ? order : "asc"}
                    onClick={() => handleSort("active")}
                  >
                    Status
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ width: 48, py: 2 }} />
              </TableRow>
              <TableRow
                sx={{
                  bgcolor:
                    theme.palette.mode === "light" ? "grey.100" : "grey.800",
                }}
              >
                <TableCell sx={{ py: 1 }}>
                  <MembershipColumnFilterField
                    value={fieldFilters.name}
                    onChange={(v) => updateFieldFilter("name", v)}
                    aria-label="Filter by name"
                  />
                </TableCell>
                <TableCell sx={{ py: 1 }}>
                  <MembershipColumnFilterField
                    value={fieldFilters.description}
                    onChange={(v) => updateFieldFilter("description", v)}
                    aria-label="Filter by description"
                  />
                </TableCell>
                <TableCell sx={{ py: 1 }}>
                  <MembershipColumnFilterField
                    value={fieldFilters.businessType}
                    onChange={(v) => updateFieldFilter("businessType", v)}
                    aria-label="Filter by industry"
                  />
                </TableCell>
                <TableCell sx={{ py: 1 }}>
                  <MembershipColumnFilterField
                    value={fieldFilters.status}
                    onChange={(v) => updateFieldFilter("status", v)}
                    placeholder="Active…"
                    aria-label="Filter by status"
                  />
                </TableCell>
                <TableCell sx={{ py: 1 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                [...Array(5)].map((_, i) => (
                  <TableRow key={i}>
                    <TableCell>
                      <Skeleton variant="text" width="60%" />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" width={180} />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" width={80} />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" width={80} />
                    </TableCell>
                    <TableCell />
                  </TableRow>
                ))
              ) : isEmpty ? (
                <TableRow>
                  <TableCell colSpan={5} sx={{ py: 6, textAlign: "center" }}>
                    <Typography color="text.secondary">
                      No plans found.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : noSearchResults ? (
                <TableRow>
                  <TableCell colSpan={5} sx={{ py: 6, textAlign: "center" }}>
                    <Typography color="text.secondary">
                      No plans match the current column filters.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginated.map((p, index) => (
                  <TableRow
                    key={p.planId}
                    hover
                    sx={{
                      ...adminTableRowSx(index),
                      cursor: "pointer",
                      "&:hover .row-arrow": { opacity: 1 },
                    }}
                    onClick={() => navigate(getPlanDetailPath(p.planId))}
                  >
                    <TableCell sx={{ py: 2, fontWeight: 500 }}>
                      {p.name}
                    </TableCell>
                    <TableCell
                      sx={{
                        py: 2,
                        color: "text.secondary",
                        maxWidth: { md: 480 },
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {p.description || "—"}
                    </TableCell>
                    <TableCell sx={{ py: 2, fontSize: "0.875rem" }}>
                      {formatBusinessTypeLabel(p.businessType)}
                    </TableCell>
                    <TableCell sx={{ py: 2 }}>
                      <Chip
                        label={p.isActive ? "Active" : "Inactive"}
                        size="small"
                        color={p.isActive ? "success" : "default"}
                      />
                    </TableCell>
                    <TableCell sx={{ py: 2, width: 48 }}>
                      <ArrowForwardIos
                        className="row-arrow"
                        sx={{
                          fontSize: 14,
                          color: "primary.main",
                          opacity: 0,
                          transition: "opacity 0.2s ease",
                        }}
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {!isLoading && !isEmpty && sorted.length > 0 && (
          <TablePagination
            component="div"
            count={sorted.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
            labelRowsPerPage="Rows:"
            sx={{ borderTop: 1, borderColor: "divider" }}
          />
        )}
      </Paper>
    </Box>
  );
};

export default PlanListTable;
